import React, { useEffect, useRef, useState, useMemo } from "react";
import io from "socket.io-client";
import MasterLayout from "../../masterLayout/MasterLayout";
import LiveChartModal from "../../components/Trading/LiveChartModal";
import useApi from "../../hook/useApi";

const SOCKET_URL = process.env.REACT_APP_API_URL || "http://localhost:5001";

const MarketChartWithSocketIO = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [allSymbols, setAllSymbols] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [selectedSymbol, setSelectedSymbol] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSegment, setSelectedSegment] = useState("ALL");
  const [segments, setSegments] = useState(["ALL"]);
  const { AsyncGetApiCall } = useApi();

  useEffect(() => {
    const socket = io(SOCKET_URL, { reconnection: true });
    socket.on("connect", () => setIsConnected(true));
    socket.on("disconnect", () => setIsConnected(false));
    socket.on("marketData", (msg) => {
      try {
        let clean = msg.replace(/^SymbolData\(/, "").replace(/\)$/, "");
        const pairs = clean.split(/,\s*/);
        const obj = {};
        pairs.forEach((p) => {
          const splitIdx = p.indexOf("=");
          if (splitIdx === -1) return;
          const k = p.substring(0, splitIdx).trim();
          let v = p.substring(splitIdx + 1).trim();
          if (!isNaN(v) && v !== "" && v !== "null") v = parseFloat(v);
          else if (v === "null") v = null;
          obj[k] = v;
          if (k === "s" || k === "symbol") obj.symbol = v;
          if (k === "b") obj.bid = v;
          if (k === "a") obj.ask = v;
          if (k === "l") obj.ltp = v;
          if (k === "h") obj.high = v;
          if (k === "lo") obj.low = v;
          if (k === "o") obj.open = v;
          if (k === "c") obj.close = v;
          if (k === "v") obj.volume = v;
          if (k === "ch") obj.ch = v;
          if (k === "cp" || k === "chp") obj.chp = v;
          if (k === "exchange") obj.exchange = v;
          if (k === "name") obj.name = v;
          if (k === "segment") obj.segment = v;
        });

        if (obj.symbol) {
          if (!obj.segment) obj.segment = obj.exchange || "OTHER";
          setAllSymbols((prev) => {
            const old = prev[obj.symbol] || {};
            obj.prevLtp = old.ltp;
            obj.prevBid = old.bid;
            obj.prevAsk = old.ask;
            return { ...prev, [obj.symbol]: { ...old, ...obj } };
          });
        }
      } catch (e) {}
    });
    return () => socket.disconnect();
  }, []);

  useEffect(() => {
    const fetchSegments = async () => {
      try {
        const res = await AsyncGetApiCall("/trading-settings/segments/list");
        const dataArr = Array.isArray(res) ? res : res?.data || [];
        setSegments(["ALL", ...dataArr.map((s) => s.name)]);
      } catch (e) {
        console.error("Failed to fetch segments", e);
      }
    };
    fetchSegments();
  }, []);

  const filteredSymbols = useMemo(() => {
    return Object.values(allSymbols).filter((s) => {
      const matchSearch = s.symbol
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
      const matchSegment =
        selectedSegment === "ALL" ||
        s.exchange === selectedSegment ||
        s.segment === selectedSegment;
      return matchSearch && matchSegment;
    });
  }, [allSymbols, searchQuery, selectedSegment]);

  const fmt = (v, d = 2) => {
    if (v == null || isNaN(v)) return "---";
    return Number(v).toFixed(v < 10 && v > 0 ? 4 : d);
  };

  const activeData = allSymbols[selectedSymbol] || {};
  // console.log("allSymbols-=-=-==>>>", allSymbols);
  // console.log("activeData-=-=-==>>>", filteredSymbols);

  const getFlickerClass = (current, prev) => {
    if (!prev || current === prev) return "bg-dark shadow-none";
    return current > prev ? "bg-primary shadow-sm" : "bg-danger shadow-sm";
  };

  return (
    <MasterLayout>
      <div
        className="bg-dark p-20 radius-12 shadow-lg"
        style={{ minHeight: "85vh" }}
      >
        <div className="d-flex justify-content-between align-items-center mb-4 gap-3 flex-wrap">
          <div className="d-flex gap-2">
            <select
              className="form-select bg-secondary border-0 text-white fw-bold radius-8 px-12"
              style={{ minWidth: "160px" }}
              value={selectedSegment}
              onChange={(e) => setSelectedSegment(e.target.value)}
            >
              {segments.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div
            className="flex-grow-1 position-relative"
            style={{ maxWidth: "400px" }}
          >
            <span className="position-absolute start-0 top-50 translate-middle-y ms-3 text-secondary">
              <i className="ri-search-line"></i>
            </span>
            <input
              type="text"
              placeholder="Search Script"
              className="form-control bg-secondary border-0 text-white ps-5 radius-8"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="ms-auto d-flex align-items-center gap-3">
            <span
              className={`badge ${isConnected ? "bg-success" : "bg-danger"} rounded-pill`}
            >
              {isConnected ? "LIVE" : "DISCONNECTED"}
            </span>
          </div>
        </div>

        <div className="table-responsive border border-secondary border-opacity-25 radius-12">
          <table
            className="table table-dark table-hover align-middle mb-0"
            style={{ "--bs-table-bg": "transparent", fontSize: "12px" }}
          >
            <thead className="bg-secondary bg-opacity-10 text-uppercase border-bottom border-secondary border-opacity-50">
              <tr>
                <th className="ps-3 py-3">Symbol</th>
                <th className="text-center">Bid</th>{" "}
                <th className="text-center">Ask</th>{" "}
                <th className="text-center">LTP</th>
                <th className="text-center">CHG%</th>{" "}
                <th className="text-center">Net Chg</th>
                <th className="text-center">High</th>{" "}
                <th className="text-center">Low</th>{" "}
                <th className="pe-3 text-center">Chart</th>
              </tr>
            </thead>
            <tbody>
              {filteredSymbols.length === 0 ? (
                <tr>
                  <td colSpan="9" className="text-center py-5 text-secondary">
                    No matching scripts found...
                  </td>
                </tr>
              ) : (
                filteredSymbols.map((s) => (
                  <tr
                    key={s.symbol}
                    className="border-bottom border-secondary border-opacity-10"
                  >
                    <td className="ps-3 fw-bold text-nowrap">{s.symbol}</td>
                    <td className="text-center px-1">
                      <div
                        className={`p-2 radius-4 transition-all ${getFlickerClass(s.bid, s.prevBid)}`}
                        style={{ minWidth: "75px" }}
                      >
                        {fmt(s.bid)}
                      </div>
                    </td>
                    <td className="text-center px-1">
                      <div
                        className={`p-2 radius-4 transition-all ${getFlickerClass(s.ask, s.prevAsk)}`}
                        style={{ minWidth: "75px" }}
                      >
                        {fmt(s.ask)}
                      </div>
                    </td>
                    <td className="text-center px-1">
                      <div
                        className={`p-2 radius-4 transition-all ${getFlickerClass(s.ltp, s.prevLtp)}`}
                        style={{ minWidth: "75px" }}
                      >
                        {fmt(s.ltp)}
                      </div>
                    </td>
                    <td
                      className={`text-center fw-bold ${s.chp >= 0 ? "text-success" : "text-danger"}`}
                    >
                      {s.chp > 0 ? "+" : ""}
                      {fmt(s.chp)}%
                    </td>
                    <td
                      className={`text-center ${s.ch >= 0 ? "text-success" : "text-danger"}`}
                    >
                      {s.ch > 0 ? "+" : ""}
                      {fmt(s.ch)}
                    </td>
                    <td className="text-center text-secondary">
                      {fmt(s.high)}
                    </td>{" "}
                    <td className="text-center text-secondary">{fmt(s.low)}</td>
                    <td className="pe-3 text-center">
                      <button
                        className="btn btn-outline-info btn-sm radius-8"
                        onClick={() => {
                          setSelectedSymbol(s.symbol);
                          setShowModal(true);
                        }}
                      >
                        <i className="ri-line-chart-line"></i>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <LiveChartModal
        show={showModal}
        onHide={() => setShowModal(false)}
        symbol={selectedSymbol}
        data={activeData}
      />
    </MasterLayout>
  );
};

export default MarketChartWithSocketIO;
